import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Check } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AuditIntake from "@/components/AuditIntake";
import { getAccountMe, getIntakeStatus } from "@/lib/api";

/**
 * Reached from My Account's "Complete your audit intake" CTA, not just the
 * one-shot post-checkout redirect - a buyer who closes that tab still has a
 * durable way back into the form for a purchase they already made.
 */
export default function AuditIntakePage() {
  const { sessionId } = useParams();
  const [state, setState] = useState({ status: "loading", email: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [account, intake] = await Promise.all([getAccountMe(), getIntakeStatus(sessionId)]);
        if (cancelled) return;
        if (intake.submitted) {
          setState({ status: "already_done", email: account.email });
        } else {
          setState({ status: "ready", email: account.email });
        }
      } catch {
        if (!cancelled) setState({ status: "error", email: null });
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <>
      <Nav />
      <div className="pt-32 pb-24 max-w-[900px] mx-auto px-6 md:px-10">
        <Link to="/account" className="tick hover:text-[#171717] flex items-center gap-2 transition-colors mb-8" data-testid="intake-back-link">
          <ArrowLeft size={12} /> Back to my account
        </Link>

        {state.status === "loading" && (
          <div className="flex items-center gap-3 text-[#404040] font-mono-tech text-[13px]">
            <Loader2 size={16} className="animate-spin text-[#e88a51]" /> Loading…
          </div>
        )}

        {state.status === "error" && (
          <div className="card-cream p-8">
            <p className="font-mono-tech text-[13px] text-[#9a3412]">
              Could not load this audit. Sign in with the same account you purchased with, or contact the analyst.
            </p>
          </div>
        )}

        {state.status === "already_done" && (
          <div className="card-cream p-8" data-testid="intake-already-done">
            <div className="tick mb-3 flex items-center gap-2 text-[#9a3412]">
              <Check size={12} /> Intake already received
            </div>
            <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed">
              This purchase already has an intake on file. The analyst has what it needs — no further action here.
            </p>
          </div>
        )}

        {state.status === "ready" && (
          <AuditIntake sessionId={sessionId} prefillEmail={state.email} />
        )}
      </div>
      <Footer />
    </>
  );
}
